import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react"
import { PlusIcon } from "@heroicons/react/24/outline"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Button } from "antd"
import { Button as CustomButton } from "components"
import { useGet } from "utils/useFetch"
import { ewaAxiosInstance } from "utils/request"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import {
  getDaysInDateRange,
  invalidateShiftPlanQueries,
  parseApiDate,
  to12HourLabel,
  toIsoDateString,
  WEEK_DAYS as DAYS,
  type DayOfWeek,
} from "../../utils/shiftTablesUtils"
import type {
  IShiftPeriodsRes,
  IShiftPeriodDoc,
  IRosteredAssignmentsTableDoc,
} from "../../typings"
import type {
  CreateShiftTableBody,
  IShiftTableDoc,
  IRosteredAssignmentsRes,
  IAnyDayAssignmentsRes,
  RosteredBulkBody,
  AnyDayBulkBody,
} from "../../typings"
import DateInput, { DateInputValue } from "./DateInput"
import ShiftTableCard, {
  ShiftTable,
  Employee,
  ShiftPeriodOption,
} from "../../components/ShiftTableCard"
import RosterTableCard, {
  RosterTable,
  RosterShiftPeriod,
} from "./RosterTableCard"
import StaffSelectionDrawer from "../../components/StaffSelectionDrawer"
import CustomPagination from "./CustomPagination"
import CreateShiftPlanTablesSkeleton from "../../components/CreateShiftPlanTablesSkeleton"

// ─── Types ────────────────────────────────────────────────────────────────────

type RosterSlotIds = Record<
  string,
  Record<string, Partial<Record<DayOfWeek, number[]>>>
>
type DaysMap = RosteredBulkBody["data"][0]["assignments"][0]["days"]

export type RosterTableWithServerId = RosterTable & { shiftTableId?: number }
export type ShiftTableWithServerId = ShiftTable & { shiftTableId?: number }

export interface AnyDayBulkPayload {
  employees: { id: number; shiftPeriodIds: number[]; weeklyLimit: number }[]
  dutyLocationId?: string
}

export interface CreateShiftPlanTablesRef {
  getAnytimeBulkPayload: () => AnyDayBulkPayload | null
}

interface CreateShiftPlanTablesProps {
  shiftPlanName?: string
  shiftType?: "rostered" | "anytime"
  shiftPlanId?: number
  dutyLocationId?: number | "workplace"
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

/** Format a DateInputValue as "Jun 14" for duration labels. */
const formatShortDate = (d?: DateInputValue): string => {
  if (!d?.dd || !d?.mm || !d?.yyyy) return ""
  const month = MONTHS_SHORT[Number(d.mm) - 1] ?? d.mm
  return `${month} ${d.dd}`
}

const createEmptySchedule = (): Record<
  DayOfWeek,
  { staffCount: number | null }
> =>
  Object.fromEntries(DAYS.map((d) => [d, { staffCount: null }])) as Record<
    DayOfWeek,
    { staffCount: number | null }
  >

function buildAssignmentUrl(
  shiftPlanId: number,
  mode: "rostered-assignments" | "anyday-assignments",
  page: number,
  dutyLocId?: number | string
): string {
  const params = new URLSearchParams({
    page: String(page),
    limit: "10",
  })

  if (dutyLocId && dutyLocId !== "workplace") {
    params.append("dutyLocationId", String(dutyLocId))
  }

  return `/v1/time-attendance/shift-plans/${shiftPlanId}/shift-tables/${mode}?${params}`
}

// ─── Component ────────────────────────────────────────────────────────────────

const CreateShiftPlanTables = forwardRef<
  CreateShiftPlanTablesRef,
  CreateShiftPlanTablesProps & { workplaceId?: number }
>(function CreateShiftPlanTables(
  { shiftType, shiftPlanId, dutyLocationId, workplaceId },
  ref
) {
  const isRoster = shiftType !== "anytime"
  const dutyLocId =
    dutyLocationId === "workplace" ? "workplace" : Number(dutyLocationId)
  // const dutyLocId = dutyLocationId != null ? Number(dutyLocationId) : undefined

  // ── State ──────────────────────────────────────────────────────────────────

  // Store assignments per duty location
  const [assignmentsByLocation, setAssignmentsByLocation] = useState<
    Record<
      string | number,
      {
        rosterTables: RosterTableWithServerId[]
        anytimeTables: ShiftTableWithServerId[]
        rosterSlotEmployeeIds: RosterSlotIds
      }
    >
  >({})

  // Current location's assignments
  const currentLocationKey = dutyLocId === "workplace" ? "workplace" : dutyLocId
  const currentLocationData = assignmentsByLocation[currentLocationKey] || {
    rosterTables: [],
    anytimeTables: [],
    rosterSlotEmployeeIds: {},
  }

  const [rosterTables, setRosterTables] = useState<RosterTableWithServerId[]>(
    currentLocationData.rosterTables
  )
  const [anytimeTables, setAnytimeTables] = useState<ShiftTableWithServerId[]>(
    currentLocationData.anytimeTables
  )
  const [rosterSlotEmployeeIds, setRosterSlotEmployeeIds] =
    useState<RosterSlotIds>(currentLocationData.rosterSlotEmployeeIds)
  const [createTableDates, setCreateTableDates] = useState<{
    startDate: DateInputValue
    endDate: DateInputValue
  } | null>(null)
  const [activeAnytimeTableId, setActiveAnytimeTableId] = useState<
    string | null
  >(null)
  const [deletingTableId, setDeletingTableId] = useState<string | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [activeSlot, setActiveSlot] = useState<{
    tableId: string
    periodId: string
    day: DayOfWeek
  } | null>(null)
  const [rosterPage, setRosterPage] = useState(1)
  const [anytimePage, setAnytimePage] = useState(1)

  const createFormRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { toast } = useAppNotificationStore()
  const [canManageAssignments] = usePermissions({
    allowedPermissions: [
      TimeAndAttendancePermission.CanManageEmployeeAssignments,
    ],
  })

  // ── Sync local state with location-based storage ────────────────────────────

  // When dutyLocationId changes, load assignments for that location
  useEffect(() => {
    const locationData = assignmentsByLocation[currentLocationKey]
    if (locationData) {
      setRosterTables(locationData.rosterTables)
      setAnytimeTables(locationData.anytimeTables)
      setRosterSlotEmployeeIds(locationData.rosterSlotEmployeeIds)
    } else {
      // Initialize empty state for new location
      setRosterTables([])
      setAnytimeTables([])
      setRosterSlotEmployeeIds({})
    }
  }, [dutyLocId])

  // Persist local state changes back to location storage
  useEffect(() => {
    setAssignmentsByLocation((prev) => ({
      ...prev,
      [currentLocationKey]: {
        rosterTables,
        anytimeTables,
        rosterSlotEmployeeIds,
      },
    }))
  }, [rosterTables, anytimeTables, rosterSlotEmployeeIds])

  // ── URLs ───────────────────────────────────────────────────────────────────

  const shiftPeriodsUrl = `/v1/time-attendance/shift-periods?page=1&limit=50&shiftPlanId=${
    shiftPlanId ?? ""
  }`

  const rosteredAssignmentsUrl =
    shiftPlanId != null
      ? buildAssignmentUrl(
          shiftPlanId,
          "rostered-assignments",
          rosterPage,
          dutyLocId
        )
      : ""

  const anyDayAssignmentsUrl =
    shiftPlanId != null
      ? buildAssignmentUrl(
          shiftPlanId,
          "anyday-assignments",
          anytimePage,
          dutyLocId
        )
      : ""

  // ── Queries ────────────────────────────────────────────────────────────────

  const { data: shiftPeriodsRes, isLoading: isShiftPeriodsLoading } =
    useGet<IShiftPeriodsRes>({
      url: shiftPeriodsUrl,
      instanceKey: "ewaAxiosInstance",
      enabled: !!shiftPlanId,
    })

  const {
    data: rosteredAssignmentsRes,
    isLoading: isRosteredAssignmentsLoading,
  } = useGet<IRosteredAssignmentsRes>({
    url: rosteredAssignmentsUrl,
    instanceKey: "ewaAxiosInstance",
    enabled: isRoster && !!shiftPlanId,
  })

  const { data: anyDayAssignmentsRes, isLoading: isAnyDayAssignmentsLoading } =
    useGet<IAnyDayAssignmentsRes>({
      url: anyDayAssignmentsUrl,
      instanceKey: "ewaAxiosInstance",
      enabled: !isRoster && !!shiftPlanId,
    })

  const shiftPeriodDocs: IShiftPeriodDoc[] = shiftPeriodsRes?.data?.docs ?? []
  const rosteredDocs = rosteredAssignmentsRes?.data?.docs ?? []
  const anyDayDocs = anyDayAssignmentsRes?.data?.docs ?? []

  const anytimeShiftPeriods: ShiftPeriodOption[] = React.useMemo(
    () =>
      shiftPeriodDocs.map((doc) => ({
        id: doc.id,
        title: doc.title,
        startTime: doc.startTime,
        endTime: doc.endTime,
      })),
    [shiftPeriodDocs]
  )

  const rosterPeriodsTemplate: RosterShiftPeriod[] = React.useMemo(
    () =>
      shiftPeriodDocs.map((doc) => ({
        id: String(doc.id),
        name: doc.title,
        startTime: to12HourLabel(doc.startTime),
        endTime: to12HourLabel(doc.endTime),
        schedule: createEmptySchedule(),
      })),
    [shiftPeriodDocs]
  )

  // ── Cache invalidation helpers ─────────────────────────────────────────────

  const invalidateShiftPlans = () => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        typeof query.queryKey?.[0] === "string" &&
        (query.queryKey[0] as string).startsWith(
          "/v1/time-attendance/shift-plans"
        ),
    })
    invalidateShiftPlanQueries(queryClient)
  }

  const invalidateShiftAssignments = (mode: "rostered" | "anyday") => {
    const needle =
      mode === "rostered" ? "rostered-assignments" : "anyday-assignments"
    queryClient.invalidateQueries({
      predicate: (q) =>
        typeof q.queryKey?.[0] === "string" &&
        (q.queryKey[0] as string).includes(needle),
    })
  }

  // ── Mutations ──────────────────────────────────────────────────────────────

  const { mutate: createShiftTable, isLoading: isCreatingTable } = useMutation({
    mutationFn: async (body: CreateShiftTableBody) => {
      if (!shiftPlanId) throw new Error("Missing shift plan id")
      const res = await ewaAxiosInstance.post<{ data: IShiftTableDoc }>(
        `/v1/time-attendance/shift-plans/${shiftPlanId}/shift-tables`,
        body
      )
      return res.data?.data
    },
    onSuccess: (table) => {
      if (!table) return
      invalidateShiftPlans()
      invalidateShiftAssignments("rostered")
      invalidateShiftAssignments("anyday")
      setCreateTableDates(null)

      const start = parseApiDate(table.startDate)
      const end = parseApiDate(table.endDate)

      if (isRoster) {
        const newTable: RosterTableWithServerId = {
          id: String(table.id),
          shiftTableId: table.id,
          dateLabel: formatShortDate(start.value),
          endDateLabel: formatShortDate(end.value),
          startDate: start.value,
          endDate: end.value,
          shiftPeriods: rosterPeriodsTemplate.map((p) => ({
            ...p,
            schedule: createEmptySchedule(),
          })),
          collapsed: false,
        }
        setRosterTables((prev) => [...prev, newTable])
        setRosterSlotEmployeeIds((prev) => ({
          ...prev,
          [String(table.id)]: {},
        }))
      } else {
        const newTable: ShiftTableWithServerId = {
          id: String(table.id),
          shiftTableId: table.id,
          dateLabel: formatShortDate(start.value),
          endDateLabel: "",
          startDate: start.value,
          endDate: { dd: "", mm: "", yyyy: "" },
          employees: [],
          collapsed: false,
        }
        setAnytimeTables((prev) => [...prev, newTable])
      }
      toast.success("Request successful.")
    },
    onError: onQueryError,
  })

  const { mutate: editShiftTable } = useMutation({
    mutationFn: async ({
      shiftTableId,
      body,
    }: {
      shiftTableId: number
      body: { dutyLocationId?: number; startDate: string; endDate: string }
    }) => {
      const res = await ewaAxiosInstance.put(
        `/v1/time-attendance/shift-tables/${shiftTableId}`,
        body
      )
      return res
    },
    onSuccess: () => {
      invalidateShiftPlans()
      invalidateShiftAssignments("rostered")
      invalidateShiftAssignments("anyday")
      toast.success("Shift table updated successfully.")
    },
    onError: onQueryError,
  })

  const { mutate: saveRosteredBulk, isLoading: isSavingRostered } = useMutation(
    {
      mutationFn: (body: RosteredBulkBody) =>
        ewaAxiosInstance.post(
          "/v1/time-attendance/shift-tables/shift-assignments/rostered/bulk",
          body
        ),
      onSuccess: (_res, variables) => {
        const hasRemoved =
          Array.isArray(variables?.removeAssignments) &&
          variables.removeAssignments.length > 0
        const hasAdded = !!variables?.data?.some((t) =>
          (t.assignments ?? []).some((p) =>
            Object.values(p.days ?? {}).some(
              (d: any) =>
                Array.isArray(d?.employeeIds) && d.employeeIds.length > 0
            )
          )
        )
        toast.success(
          hasRemoved && !hasAdded
            ? "Remove Assignment successfully."
            : hasRemoved && hasAdded
            ? "Assignments updated successfully."
            : "Assignment added successfully."
        )
        invalidateShiftAssignments("rostered")
        invalidateShiftPlans()
      },
      onError: onQueryError,
    }
  )

  const { mutate: saveAnyDayBulk, isLoading: isSavingAnyDay } = useMutation({
    mutationFn: (body: AnyDayBulkBody) =>
      ewaAxiosInstance.post(
        "/v1/time-attendance/shift-tables/shift-assignments/any-day/bulk",
        body
      ),
    onSuccess: (_res, variables) => {
      const hasRemoved =
        Array.isArray(variables?.removeAssignments) &&
        variables.removeAssignments.length > 0
      const hasAdded = !!variables?.data?.some(
        (t) => Array.isArray(t.assignments) && t.assignments.length > 0
      )
      toast.success(
        hasRemoved && !hasAdded
          ? "Remove Assignment successfully."
          : hasRemoved && hasAdded
          ? "Assignments updated successfully."
          : "Assignment added successfully."
      )
      invalidateShiftAssignments("anyday")
      invalidateShiftPlans()
    },
    onError: onQueryError,
  })

  // ── Effects ────────────────────────────────────────────────────────────────

  React.useEffect(() => {
    if (createTableDates != null) {
      createFormRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
    }
  }, [createTableDates])

  React.useEffect(() => {
    if (!isRoster || !rosterPeriodsTemplate.length || rosteredDocs.length === 0)
      return

    const allSlotIds: RosterSlotIds = {}

    const tables: RosterTableWithServerId[] = rosteredDocs.map((doc) => {
      const start = parseApiDate(doc.startDate)
      const end = parseApiDate(doc.endDate)

      const assignmentMap: Record<
        string,
        Partial<Record<DayOfWeek, number[]>>
      > = {}
      for (const a of (doc as IRosteredAssignmentsTableDoc).assignments ?? []) {
        const key = String(a.shiftPeriodId)
        if (assignmentMap[key]) continue
        const dayToIds: Partial<Record<DayOfWeek, number[]>> = {}
        for (const day of DAYS) {
          const arr = (a.days as Record<string, { employeeId: number }[]>)?.[
            day.toLowerCase()
          ]
          dayToIds[day] = Array.isArray(arr) ? arr.map((x) => x.employeeId) : []
        }
        assignmentMap[key] = dayToIds
      }

      const slotIds: Record<string, Partial<Record<DayOfWeek, number[]>>> = {}
      const shiftPeriods: RosterShiftPeriod[] = rosterPeriodsTemplate.map(
        (p) => {
          const schedule = {} as Record<
            DayOfWeek,
            { staffCount: number | null }
          >
          const periodKey = String(p.id)
          const dayToIds = assignmentMap[periodKey] ?? {}
          for (const day of DAYS) {
            const ids = dayToIds[day] ?? []
            if (!slotIds[periodKey]) slotIds[periodKey] = {}
            slotIds[periodKey][day] = ids
            schedule[day] = { staffCount: ids.length > 0 ? ids.length : null }
          }
          return { ...p, schedule }
        }
      )

      const tableId = String(doc.id)
      allSlotIds[tableId] = slotIds
      return {
        id: tableId,
        shiftTableId: doc.id,
        dateLabel: formatShortDate(start.value),
        endDateLabel: formatShortDate(end.value),
        startDate: start.value,
        endDate: end.value,
        shiftPeriods,
        collapsed: false,
      }
    })

    setRosterSlotEmployeeIds((prev) => ({ ...prev, ...allSlotIds }))
    setRosterTables(tables)
  }, [isRoster, rosteredDocs, rosterPeriodsTemplate])

  React.useEffect(() => {
    if (isRoster || !anyDayDocs.length) return
    setAnytimeTables(
      anyDayDocs.map((doc) => {
        const start = parseApiDate(doc.startDate)
        const end = parseApiDate(doc.endDate)
        const employees: Employee[] = (doc.assignments ?? []).map((a) => ({
          id: String(a.employeeId),
          name: a.employee?.fullName ?? `Employee ${a.employeeId}`,
          eligibleShifts: a.anydayEligibileShifts?.length ?? 0,
          shiftPeriodIds:
            a.anydayEligibileShifts?.map((s) => s.shiftPeriodId) ?? [],
          shiftsRequired: a.weeklyLimit ?? 5,
          selected: false,
        }))
        return {
          id: String(doc.id),
          shiftTableId: doc.id,
          dateLabel: formatShortDate(start.value),
          endDateLabel: formatShortDate(end.value),
          startDate: start.value,
          endDate: end.value,
          employees,
          collapsed: false,
        }
      })
    )
  }, [isRoster, anyDayDocs])

  // ── Table CRUD helpers ─────────────────────────────────────────────────────

  const openCreateTableForm = () =>
    setCreateTableDates({
      startDate: { dd: "", mm: "", yyyy: "" },
      endDate: { dd: "", mm: "", yyyy: "" },
    })

  function deleteTable(
    tableId: string,
    shiftTableId: number | undefined,
    setter: React.Dispatch<
      React.SetStateAction<(RosterTableWithServerId | ShiftTableWithServerId)[]>
    >
  ) {
    setDeletingTableId(tableId)
    const finish = () => {
      setter((prev) => prev.filter((t) => t.id !== tableId))
      setDeletingTableId((cur) => (cur === tableId ? null : cur))
    }
    if (!shiftTableId) {
      finish()
      return
    }
    ewaAxiosInstance
      .delete(`/v1/time-attendance/shift-tables/${shiftTableId}`)
      .then(finish)
      .catch((err) => {
        onQueryError(err)
        setDeletingTableId((cur) => (cur === tableId ? null : cur))
      })
  }

  const updateTableField = <
    T extends RosterTableWithServerId | ShiftTableWithServerId
  >(
    setter: React.Dispatch<React.SetStateAction<T[]>>,
    tableId: string,
    field: "startDate" | "endDate" | "collapsed",
    value: DateInputValue | boolean
  ) =>
    setter((prev) =>
      prev.map((t) => (t.id === tableId ? { ...t, [field]: value } : t))
    )

  // ── Anytime table helpers ──────────────────────────────────────────────────

  const toggleAnytimeCollapse = (id: string) =>
    updateTableField(
      setAnytimeTables,
      id,
      "collapsed",
      !anytimeTables.find((t) => t.id === id)?.collapsed
    )

  const toggleSelectAll = (tableId: string, checked: boolean) =>
    setAnytimeTables((prev) =>
      prev.map((t) =>
        t.id !== tableId
          ? t
          : {
              ...t,
              employees: t.employees.map((e) => ({ ...e, selected: checked })),
            }
      )
    )

  const toggleEmployee = (tableId: string, empId: string) =>
    setAnytimeTables((prev) =>
      prev.map((t) =>
        t.id !== tableId
          ? t
          : {
              ...t,
              employees: t.employees.map((e) =>
                e.id === empId ? { ...e, selected: !e.selected } : e
              ),
            }
      )
    )

  const updateShiftsRequired = (
    tableId: string,
    empId: string,
    delta: number
  ) =>
    setAnytimeTables((prev) =>
      prev.map((t) =>
        t.id !== tableId
          ? t
          : {
              ...t,
              employees: t.employees.map((e) =>
                e.id === empId
                  ? {
                      ...e,
                      shiftsRequired: Math.max(
                        1,
                        Math.min(7, e.shiftsRequired + delta)
                      ),
                    }
                  : e
              ),
            }
      )
    )

  const updateEligibleShifts = (
    tableId: string,
    empId: string,
    shiftPeriodIds: number[]
  ) =>
    setAnytimeTables((prev) =>
      prev.map((t) =>
        t.id !== tableId
          ? t
          : {
              ...t,
              employees: t.employees.map((e) =>
                e.id === empId
                  ? {
                      ...e,
                      shiftPeriodIds,
                      eligibleShifts: shiftPeriodIds.length,
                    }
                  : e
              ),
            }
      )
    )

  // ── Roster table helpers ───────────────────────────────────────────────────

  const toggleRosterCollapse = (id: string) =>
    updateTableField(
      setRosterTables,
      id,
      "collapsed",
      !rosterTables.find((t) => t.id === id)?.collapsed
    )

  const openStaffDrawer = (
    tableId: string,
    periodId: string,
    day: DayOfWeek
  ) => {
    if (!canManageAssignments) {
      toast.unauthorized()
      return
    }
    // Validate prerequisites before opening the drawer:
    // - For rostered assignments we require a duty location to be selected
    //   and the shift table must have been created on the server.
    if (isRoster) {
      if (dutyLocId == null) {
        toast.error(
          "Please select a duty location in the shift plan details before assigning staff."
        )
        return
      }

      const table = rosterTables.find((t) => t.id === tableId)
      if (!table || !table.shiftTableId) {
        toast.error("Please create the shift table before assigning staff.")
        return
      }

      if (!getDaysInDateRange(table.startDate, table.endDate).has(day)) {
        toast.error(
          `${day} is outside this shift table's duration. Extend the duration to assign staff on this day.`
        )
        return
      }
    }

    setActiveSlot({ tableId, periodId, day })
    setDrawerOpen(true)
  }

  // ── Staff drawer handlers ──────────────────────────────────────────────────

  const handleStaffSave = (selectedIds: string[]) => {
    if (!activeSlot) return
    const { tableId, periodId, day } = activeSlot
    const numericIds = selectedIds.map(Number).filter((id) => !Number.isNaN(id))
    const previousEmployeeIds =
      rosterSlotEmployeeIds?.[tableId]?.[periodId]?.[day] ?? []
    const dayLower = day.toLowerCase()
    const rosterDoc = rosteredDocs.find(
      (d) => String(d.id) === String(tableId)
    ) as IRosteredAssignmentsTableDoc | undefined
    const period = rosterDoc?.assignments?.find(
      (a) => String(a.shiftPeriodId) === String(periodId)
    )
    const dayDocs = (period?.days as any)?.[dayLower] as
      | { id: number; employeeId: number }[]
      | undefined

    const existingEmployeeIds = Array.isArray(dayDocs)
      ? dayDocs.map((d) => d.employeeId)
      : previousEmployeeIds

    const nextSet = new Set(numericIds)
    const existingSet = new Set(existingEmployeeIds)
    const removedEmployeeIds = existingEmployeeIds.filter(
      (id) => !nextSet.has(id)
    )
    const addedEmployeeIds = numericIds.filter((id) => !existingSet.has(id))

    setRosterSlotEmployeeIds((prev) => ({
      ...prev,
      [tableId]: {
        ...prev[tableId],
        [periodId]: { ...prev[tableId]?.[periodId], [day]: numericIds },
      },
    }))
    setRosterTables((prev) =>
      prev.map((t) =>
        t.id !== tableId
          ? t
          : {
              ...t,
              shiftPeriods: t.shiftPeriods.map((p) =>
                p.id !== periodId
                  ? p
                  : {
                      ...p,
                      schedule: {
                        ...p.schedule,
                        [day]: {
                          staffCount:
                            numericIds.length > 0 ? numericIds.length : null,
                        },
                      },
                    }
              ),
            }
      )
    )

    if (dutyLocId == null) {
      toast.error(
        "Please select a duty location in the shift plan details before assigning staff."
      )
      return
    }
    const table = rosterTables.find((t) => t.id === tableId)
    if (!table?.shiftTableId) {
      toast.error("Please create the shift table before assigning staff.")
      return
    }

    const days: DaysMap = {} as DaysMap
    const key = dayLower as keyof DaysMap
    days[key] = { employeeIds: addedEmployeeIds }
    const removeAssignmentIds =
      removedEmployeeIds.length > 0 && Array.isArray(dayDocs)
        ? (() => {
            const removedSet = new Set(removedEmployeeIds)
            return dayDocs
              .filter((a) => removedSet.has(a.employeeId))
              .map((a) => Number(a.id))
              .filter((id) => Number.isFinite(id))
          })()
        : []

    if (addedEmployeeIds.length === 0 && removeAssignmentIds.length === 0) {
      setDrawerOpen(false)
      setActiveSlot(null)
      return
    }

    saveRosteredBulk(
      {
        dutyLocationId: dutyLocId === "workplace" ? null : dutyLocId,
        data: [
          {
            shiftTableId: table.shiftTableId,
            assignments: [{ shiftPeriodId: Number(periodId), days }],
          },
        ],
        ...(removeAssignmentIds.length > 0 && {
          removeAssignments: removeAssignmentIds,
        }),
      },
      {
        onSuccess: () => {
          setDrawerOpen(false)
          setActiveSlot(null)
        },
      }
    )
  }

  const handleDrawerSave = (selected: { id: string; name: string }[]) => {
    if (activeAnytimeTableId) {
      const table = anytimeTables.find((t) => t.id === activeAnytimeTableId)
      if (!table) return

      const selectedIds = selected.map((s) => s.id)
      const selectedSet = new Set(selectedIds)
      const existingSet = new Set(table.employees.map((e) => e.id))
      const removedEmployees = table.employees.filter(
        (e) => !selectedSet.has(e.id)
      )

      const nextEmployees: Employee[] = [
        ...table.employees.filter((e) => selectedSet.has(e.id)),
        ...selected
          .filter(({ id }) => !existingSet.has(id))
          .map(({ id, name }) => ({
            id,
            name,
            eligibleShifts: null,
            shiftPeriodIds: [],
            shiftsRequired: 1,
            selected: false,
          })),
      ]

      setAnytimeTables((prev) =>
        prev.map((t) =>
          t.id !== activeAnytimeTableId ? t : { ...t, employees: nextEmployees }
        )
      )

      if (removedEmployees.length > 0 && table.shiftTableId != null) {
        const doc = anyDayDocs.find(
          (d) => String(d.id) === String(table.shiftTableId)
        )
        const removedSet = new Set(removedEmployees.map((e) => Number(e.id)))
        const removeAssignments = (doc?.assignments ?? [])
          .filter((a) => removedSet.has(a.employeeId))
          .map((a) => Number(a.id))
          .filter((id) => Number.isFinite(id))

        if (removeAssignments.length > 0) {
          saveAnyDayBulk(
            {
              dutyLocationId: dutyLocId === "workplace" ? null : dutyLocId,
              data: [{ shiftTableId: table.shiftTableId, assignments: [] }],
              removeAssignments,
            },
            {
              onSuccess: () => {
                setDrawerOpen(false)
                setActiveAnytimeTableId(null)
                invalidateShiftPlans()
              },
            }
          )
          return
        }
      }

      setDrawerOpen(false)
      setActiveAnytimeTableId(null)
      invalidateShiftPlans()
      return
    }
    handleStaffSave(selected.map((s) => s.id))
  }

  // ── Create table submit ────────────────────────────────────────────────────

  const handleCreateTableSubmit = () => {
    if (!createTableDates) return
    const startStr = toIsoDateString(createTableDates.startDate)
    const endStr = toIsoDateString(createTableDates.endDate)
    if (!startStr || !endStr) {
      toast.error("Please enter both start and end dates (DD/MM/YY).")
      return
    }
    const body: CreateShiftTableBody = { startDate: startStr, endDate: endStr }
    if (dutyLocId !== "workplace") body.dutyLocationId = dutyLocId
    createShiftTable(body)
  }

  // ── Edit (update) shift table duration — shared by rostered & anytime ────────

  const saveTableDuration = (
    table: RosterTableWithServerId | ShiftTableWithServerId
  ): boolean => {
    const startStr = toIsoDateString(table.startDate)
    const endStr = toIsoDateString(table.endDate)
    if (!startStr || !endStr) {
      toast.error("Please enter both start and end dates (DD/MM/YY).")
      return false
    }

    // Refresh the duration labels locally for immediate feedback.
    const patch = {
      dateLabel: formatShortDate(table.startDate),
      endDateLabel: formatShortDate(table.endDate),
    }
    if (isRoster) {
      setRosterTables((prev) =>
        prev.map((t) => (t.id === table.id ? { ...t, ...patch } : t))
      )
    } else {
      setAnytimeTables((prev) =>
        prev.map((t) => (t.id === table.id ? { ...t, ...patch } : t))
      )
    }

    // Persist only when the table already exists on the server.
    if (table.shiftTableId) {
      const body: {
        dutyLocationId?: number
        startDate: string
        endDate: string
      } = { startDate: startStr, endDate: endStr }
      if (dutyLocId !== "workplace") body.dutyLocationId = dutyLocId
      editShiftTable({ shiftTableId: table.shiftTableId, body })
    }
    return true
  }

  // ── Bulk payload builders ──────────────────────────────────────────────────

  const getAnytimeBulkPayload = (): AnyDayBulkPayload | null => {
    if (!shiftPlanId) return null
    const allEmployees = anytimeTables.flatMap((t) => t.employees)
    if (!allEmployees.length) return null
    return {
      employees: allEmployees.map((emp) => ({
        id: Number(emp.id),
        shiftPeriodIds: emp.shiftPeriodIds ?? [],
        weeklyLimit: Math.min(7, Math.max(1, emp.shiftsRequired)),
      })),
      ...(dutyLocationId != null && { dutyLocationId: String(dutyLocationId) }),
    }
  }

  const buildAnyDayBulkPayload = (): AnyDayBulkBody | null => {
    const tablesWithId = anytimeTables.filter(
      (t): t is ShiftTableWithServerId & { shiftTableId: number } =>
        t.shiftTableId != null
    )
    if (!tablesWithId.length) return null
    return {
      dutyLocationId: dutyLocId === "workplace" ? null : dutyLocId,
      data: tablesWithId.map((table) => ({
        shiftTableId: table.shiftTableId,
        assignments: table.employees.map((emp) => ({
          employeeId: Number(emp.id),
          shiftPeriodIds: emp.shiftPeriodIds ?? [],
          shiftRequiredPerWeek: Math.min(7, Math.max(1, emp.shiftsRequired)),
        })),
      })),
    }
  }

  useImperativeHandle(ref, () => ({ getAnytimeBulkPayload }))

  // ── Shared empty state ─────────────────────────────────────────────────────

  const emptyState = (
    <div className="rounded-lg border-2 border-dashed border-[#CAD5E2] p-8 text-center">
      <p className="text-[#64748B] mb-2">
        No shift tables yet. Click &quot;Create shift table&quot; to create one.
      </p>
    </div>
  )

  const tableDivider = (
    <div className="border-b-[3px] border-dashed border-[#CAD5E2] my-4" />
  )

  // ── Create-table date validation (rules 1–6) ───────────────────────────────
  // Recomputed every render so the Create button stays in sync with the inputs
  // as the user types. Inlined per project preference (no helper extracted).
  const startD = createTableDates?.startDate
  const endD = createTableDates?.endDate

  // `startObj` / `endObj` are the JS Dates rebuilt from the typed components.
  // Used for rule 5 (real-calendar round-trip) and rule 6 (start ≤ end).
  // When fields are empty or out-of-range, the round-trip below fails so an
  // Invalid Date never sneaks through.
  const startObj = startD
    ? new Date(Number(startD.yyyy), Number(startD.mm) - 1, Number(startD.dd))
    : null
  const endObj = endD
    ? new Date(Number(endD.yyyy), Number(endD.mm) - 1, Number(endD.dd))
    : null

  const currentYear = new Date().getFullYear()

  const startValid =
    !!startD &&
    startD.dd.length >= 1 &&
    startD.mm.length >= 1 &&
    startD.yyyy.length === 4 &&
    Number(startD.dd) >= 1 &&
    Number(startD.dd) <= 31 &&
    Number(startD.mm) >= 1 &&
    Number(startD.mm) <= 12 &&
    Number(startD.yyyy) >= currentYear &&
    Number(startD.yyyy) <= 2100 &&
    startObj?.getFullYear() === Number(startD.yyyy) &&
    startObj?.getMonth() === Number(startD.mm) - 1 &&
    startObj?.getDate() === Number(startD.dd)

  const endValid =
    !!endD &&
    endD.dd.length >= 1 &&
    endD.mm.length >= 1 &&
    endD.yyyy.length === 4 &&
    Number(endD.dd) >= 1 &&
    Number(endD.dd) <= 31 &&
    Number(endD.mm) >= 1 &&
    Number(endD.mm) <= 12 &&
    Number(endD.yyyy) >= currentYear &&
    Number(endD.yyyy) <= 2100 &&
    endObj?.getFullYear() === Number(endD.yyyy) &&
    endObj?.getMonth() === Number(endD.mm) - 1 &&
    endObj?.getDate() === Number(endD.dd)

  const rangeValid =
    startValid && endValid && startObj!.getTime() <= endObj!.getTime()

  const canCreateTable = startValid && endValid && rangeValid

  const startTouched = !!(startD?.dd || startD?.mm || startD?.yyyy)
  const endTouched = !!(endD?.dd || endD?.mm || endD?.yyyy)

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center flex-wrap gap-2">
        <Button
          color="default"
          className="font-semibold h-10 rounded-lg border-2 border-[#004AF5] text-[#004AF5] bg-white hover:!border-[#004AF5] hover:!text-[#004AF5] hover:!bg-[#F8FAFC] flex items-center gap-2"
          onClick={openCreateTableForm}
        >
          <PlusIcon className="w-4 h-4" />
          Create shift table
        </Button>
      </div>

      {/* Create table inline form */}
      {createTableDates != null && (
        <div
          ref={createFormRef}
          className="rounded-lg border-2 border-[#E2E8F0] p-4 bg-[#F8FAFC] flex flex-col gap-4"
        >
          <p className="text-sm font-semibold text-[#0F172B]">
            New shift table
          </p>
          <div className="flex flex-wrap items-end gap-4 pb-6">
            {(["startDate", "endDate"] as const).map((field) => {
              const fieldValid = field === "startDate" ? startValid : endValid
              const fieldTouched =
                field === "startDate" ? startTouched : endTouched
              const showError = fieldTouched && !fieldValid

              const fieldData = createTableDates[field]
              const yearVal = Number(fieldData?.yyyy || 0)
              const isPastYear =
                fieldTouched && yearVal > 0 && yearVal < currentYear

              return (
                <div
                  key={field}
                  className="relative"
                >
                  <label className="block text-sm text-[#64748B] mb-1 capitalize">
                    {field === "startDate" ? "Start date" : "End date"}
                  </label>
                  <DateInput
                    value={createTableDates[field]}
                    onChange={(val) =>
                      setCreateTableDates((prev) =>
                        prev ? { ...prev, [field]: val } : null
                      )
                    }
                  />
                  {showError && (
                    <p className="absolute left-0 top-full mt-1 text-xs text-red-500 whitespace-nowrap">
                      {isPastYear
                        ? "Past years not allowed"
                        : "Enter a valid date"}
                    </p>
                  )}
                </div>
              )
            })}
            <div className="flex gap-2">
              <Button
                type="primary"
                className="font-semibold h-10 rounded-lg bg-[#004AF5]"
                loading={isCreatingTable}
                disabled={!canCreateTable}
                onClick={handleCreateTableSubmit}
              >
                Create shift table
              </Button>
              <Button
                type="default"
                className="font-semibold h-10 rounded-lg"
                onClick={() => setCreateTableDates(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
          {startValid && endValid && !rangeValid && (
            <p className="text-xs text-red-500">
              End date must be on or after start date
            </p>
          )}
        </div>
      )}

      {/* Tables list */}
      <div className="flex flex-col gap-0">
        {isRoster ? (
          !!shiftPlanId &&
          (isShiftPeriodsLoading || isRosteredAssignmentsLoading) ? (
            <CreateShiftPlanTablesSkeleton variant="rostered" />
          ) : rosterTables.length === 0 && createTableDates == null ? (
            emptyState
          ) : (
            <>
              {rosterTables.map((table) => (
                <React.Fragment key={table.id}>
                  {tableDivider}
                  <p className="text-sm text-[#0F172B] mb-2">
                    {table.dateLabel}
                    {table.endDateLabel ? ` to ${table.endDateLabel}` : ""}
                  </p>
                  <RosterTableCard
                    table={table}
                    onToggleCollapse={() => toggleRosterCollapse(table.id)}
                    onDelete={() =>
                      deleteTable(
                        table.id,
                        table.shiftTableId,
                        setRosterTables as any
                      )
                    }
                    onUpdateStartDate={(val) =>
                      updateTableField(
                        setRosterTables,
                        table.id,
                        "startDate",
                        val
                      )
                    }
                    onUpdateEndDate={(val) =>
                      updateTableField(
                        setRosterTables,
                        table.id,
                        "endDate",
                        val
                      )
                    }
                    onAddStaff={(periodId, day) =>
                      openStaffDrawer(table.id, periodId, day)
                    }
                    onSaveDuration={() => saveTableDuration(table)}
                  />
                </React.Fragment>
              ))}
              {(rosteredAssignmentsRes?.data?.totalDocs ?? 0) > 10 && (
                <div className="mt-4 flex justify-end">
                  <CustomPagination
                    page={rosteredAssignmentsRes!.data.page}
                    total={rosteredAssignmentsRes!.data.totalDocs}
                    onChange={setRosterPage}
                  />
                </div>
              )}
            </>
          )
        ) : !!shiftPlanId &&
          (isShiftPeriodsLoading || isAnyDayAssignmentsLoading) ? (
          <CreateShiftPlanTablesSkeleton variant="anyday" />
        ) : anytimeTables.length === 0 && createTableDates == null ? (
          emptyState
        ) : (
          <>
            {anytimeTables.map((table) => (
              <React.Fragment key={table.id}>
                {tableDivider}
                <p className="text-sm text-[#0F172B] mb-2">
                  {table.dateLabel}
                  {table.endDateLabel ? ` to ${table.endDateLabel}` : ""}
                </p>
                <ShiftTableCard
                  table={table}
                  shiftPeriods={anytimeShiftPeriods}
                  onToggleCollapse={() => toggleAnytimeCollapse(table.id)}
                  onDelete={() =>
                    deleteTable(
                      table.id,
                      table.shiftTableId,
                      setAnytimeTables as any
                    )
                  }
                  onUpdateStartDate={(val) =>
                    updateTableField(
                      setAnytimeTables,
                      table.id,
                      "startDate",
                      val
                    )
                  }
                  onUpdateEndDate={(val) =>
                    updateTableField(setAnytimeTables, table.id, "endDate", val)
                  }
                  onAddEmployee={() => {
                    if (!canManageAssignments) {
                      toast.unauthorized()
                      return
                    }
                    setActiveAnytimeTableId(table.id)
                    setDrawerOpen(true)
                  }}
                  onToggleSelectAll={(checked) =>
                    toggleSelectAll(table.id, checked)
                  }
                  onToggleEmployee={(empId) => toggleEmployee(table.id, empId)}
                  onUpdateShiftsRequired={(empId, delta) =>
                    updateShiftsRequired(table.id, empId, delta)
                  }
                  onSelectEligibleShifts={(empId, shiftPeriodIds) =>
                    updateEligibleShifts(table.id, empId, shiftPeriodIds)
                  }
                  onSaveDuration={() => saveTableDuration(table)}
                />
              </React.Fragment>
            ))}
            {(anyDayAssignmentsRes?.data?.totalDocs ?? 0) > 10 && (
              <div className="mt-4 flex justify-end">
                <CustomPagination
                  page={anyDayAssignmentsRes!.data.page}
                  total={anyDayAssignmentsRes!.data.totalDocs}
                  onChange={setAnytimePage}
                />
              </div>
            )}
            {anytimeTables.length > 0 && (
              <div className="mt-4 flex justify-end">
                <CustomButton
                  color="primary"
                  className="font-semibold h-10"
                  submitting={isSavingAnyDay}
                  onClick={() => {
                    const body = buildAnyDayBulkPayload()
                    if (body) saveAnyDayBulk(body)
                  }}
                >
                  Save assignments
                </CustomButton>
              </div>
            )}
          </>
        )}
      </div>

      <StaffSelectionDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false)
          setActiveSlot(null)
          setActiveAnytimeTableId(null)
        }}
        workplaceId={workplaceId!}
        onSave={handleDrawerSave}
        saveLoading={
          (!!activeSlot && isSavingRostered) ||
          (!!activeAnytimeTableId && isSavingAnyDay)
        }
        preSelectedIds={
          activeSlot
            ? rosterSlotEmployeeIds[activeSlot.tableId]?.[
                activeSlot.periodId
              ]?.[activeSlot.day]?.map(String) ?? []
            : activeAnytimeTableId
            ? anytimeTables
                .find((t) => t.id === activeAnytimeTableId)
                ?.employees?.map((e) => e.id) ?? []
            : []
        }
      />
    </div>
  )
})

export default CreateShiftPlanTables
